import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Sparkles } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { StarBurst } from '@/components/HeroReward';
import { useStorage } from '@/hooks/useStorage';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export default function Home() {
  const { getTodayWords, getStats, unlockTodaySession, storage, getChildPoints } = useStorage();
  const [showReward, setShowReward] = useState(false);

  const stats = getStats();
  const todayWords = getTodayWords();
  const childPoints = getChildPoints();
  const wordCount = storage.dailyWordCount ?? 3;

  const handleStartLearning = () => {
    unlockTodaySession();
    setShowReward(true);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="container mx-auto px-4 py-6 space-y-8 max-w-5xl"
        >
          {/* Hero Banner */}
          <motion.section variants={item} className="relative overflow-hidden rounded-3xl shadow-lg">
            <div className="absolute inset-0 z-0 bg-gradient-to-br from-orange-100 via-yellow-50 to-green-100" />
            <div className="relative z-10 text-center py-12 px-6">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 18 }}
              >
                <div className="flex justify-center gap-3 mb-4">
                  <span className="text-6xl animate-bounce">🐼</span>
                  <span className="text-6xl animate-bounce" style={{ animationDelay: '0.1s' }}>🦊</span>
                  <span className="text-6xl animate-bounce" style={{ animationDelay: '0.2s' }}>🐰</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-3 text-foreground font-heading">
                  宥宥和朋友的语言家
                </h1>
                <p className="text-xl md:text-2xl text-muted-foreground font-semibold">
                  The Prophet of Yoyo
                </p>
              </motion.div>

              {stats.streak > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring', stiffness: 300 }}
                  className="inline-flex items-center gap-2 mt-4 bg-white/80 backdrop-blur px-6 py-3 rounded-full border border-border shadow-sm"
                >
                  <Flame className="w-6 h-6 text-primary" />
                  <span className="text-lg font-bold text-primary">
                    连续学习 {stats.streak} 天 🔥
                  </span>
                  <Sparkles className="w-5 h-5 text-accent" />
                </motion.div>
              )}
            </div>
          </motion.section>

          {/* Stats + Start Learning */}
          <motion.section variants={item}>
            <div className="bg-card border-2 border-border rounded-3xl p-7 shadow-sm">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">📚 今日学习</h2>
                  <p className="text-sm text-muted-foreground mt-1">每日 {wordCount} 个新单词</p>
                </div>
                <div className="flex items-center gap-2 bg-secondary rounded-2xl px-4 py-2">
                  <span className="text-lg font-bold text-primary">⚡ {childPoints} 积分</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-lang-zh-bg border-2 border-lang-zh-border rounded-2xl p-4 text-center">
                  <div className="text-3xl font-black text-lang-zh">{todayWords.length}</div>
                  <div className="text-xs font-semibold text-muted-foreground mt-1">今日单词</div>
                </div>
                <div className="bg-secondary border-2 border-border rounded-2xl p-4 text-center">
                  <div className="text-3xl font-black text-foreground">{stats.totalLearned}</div>
                  <div className="text-xs font-semibold text-muted-foreground mt-1">总掌握</div>
                </div>
                <div className="bg-lang-en-bg border-2 border-lang-en-border rounded-2xl p-4 text-center">
                  <div className="text-3xl font-black text-lang-en">🔥 {stats.streak}</div>
                  <div className="text-xs font-semibold text-muted-foreground mt-1">连续天</div>
                </div>
              </div>

              {!storage.todayUnlocked ? (
                <div className="text-center py-6">
                  <div className="text-6xl mb-4">🔐</div>
                  <p className="text-lg text-muted-foreground mb-6">
                    今天还没有开始学习哦！解锁今日 {wordCount} 个新单词 🚀
                  </p>
                  <Button
                    size="lg"
                    onClick={handleStartLearning}
                    className="text-xl px-12 py-6 rounded-2xl bg-primary hover:bg-primary/90 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    🚀 开始今日任务
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-center mb-4">
                    <span className="text-2xl font-bold text-lang-en">✅ 今日已解锁！继续学习吧</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <Link to={ROUTE_PATHS.LEARN}>
                      <Button size="lg" className="w-full rounded-2xl h-16 text-base">📚 继续学习</Button>
                    </Link>
                    <Link to={ROUTE_PATHS.REVIEW}>
                      <Button variant="outline" size="lg" className="w-full rounded-2xl h-16 text-base border-2">🔄 间隔复习</Button>
                    </Link>
                    <Link to={ROUTE_PATHS.PHONICS}>
                      <Button variant="outline" size="lg" className="w-full rounded-2xl h-16 text-base border-2">🎵 发音练习</Button>
                    </Link>
                    <Link to={ROUTE_PATHS.REWARDS}>
                      <Button variant="outline" size="lg" className="w-full rounded-2xl h-16 text-base border-2">🎁 我的奖励</Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </motion.section>

          {/* Mastery per language */}
          <motion.section variants={item}>
            <div className="bg-card border-2 border-border rounded-3xl p-6 shadow-sm">
              <h2 className="text-xl font-bold mb-4 text-foreground">📊 三语掌握程度</h2>
              <div className="space-y-4">
                {[
                  { lang: '🇨🇳 中文', mastery: stats.masteryZh, barClass: 'bg-lang-zh' },
                  { lang: '🇯🇵 日文', mastery: stats.masteryJp, barClass: 'bg-lang-jp' },
                  { lang: '🇬🇧 英文', mastery: stats.masteryEn, barClass: 'bg-lang-en' },
                ].map(({ lang, mastery, barClass }) => (
                  <div key={lang}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-semibold text-foreground">{lang}</span>
                      <span className="text-sm font-bold text-primary">{mastery}%</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${mastery}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                        className={`h-3 rounded-full ${barClass}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Quick nav cards */}
          <motion.section variants={item}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { to: ROUTE_PATHS.LEARN, emoji: '📚', label: '今日学习', desc: `${wordCount}个新单词` },
                { to: ROUTE_PATHS.REVIEW, emoji: '🔄', label: '间隔复习', desc: '巩固已学词汇' },
                { to: ROUTE_PATHS.PHONICS, emoji: '🎵', label: '发音基础', desc: '拼音·五十音·ABC' },
                { to: ROUTE_PATHS.PROGRESS, emoji: '📊', label: '学习进度', desc: '查看成就' },
                { to: ROUTE_PATHS.REWARDS, emoji: '🎁', label: '积分奖励', desc: `${childPoints}积分可兑换` },
                { to: ROUTE_PATHS.PARENT, emoji: '👨‍👩‍👧', label: '家长后台', desc: '管理学习内容' },
              ].map(({ to, emoji, label, desc }) => (
                <Link key={to} to={to}>
                  <motion.div
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-card border-2 border-border rounded-2xl p-5 text-center cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
                  >
                    <div className="text-4xl mb-2">{emoji}</div>
                    <div className="font-bold text-foreground text-base">{label}</div>
                    <div className="text-xs text-muted-foreground mt-1">{desc}</div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </motion.section>
        </motion.div>
      </div>

      <StarBurst show={showReward} onComplete={() => setShowReward(false)} />
    </Layout>
  );
}
